import { observer } from 'mobx-react';
import { useCallback, useMemo, useState } from 'react';
import { BRANCHES_AVAILABILITY_OPTION, branchesFilterOptions } from '../../constants/branches';
import branchesStore from '../../store/branchesStore';
import { DEBOUNCE_TIME } from 'src/constants/main';
import { SCREENS } from 'src/constants/screens';
import integrationsStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import client from 'src/services/client';
import { history } from 'src/services/history';
import wms from 'src/services/wms';
import { booleanToNumber, downloadFile, extractFileName, getLastArrayItem } from 'src/utils/helpers';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import Picker from 'src/view/components/Controls/Pickers/Picker/Picker';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types';
import SearchInput from 'src/view/components/Controls/SearchInput/SearchInput';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import { appStore } from '~/mobx/appStore';
import logger from '~/services/logger';
import './style.scss';

const BranchesHeader = observer((): JSX.Element => {
  // --------------- State ---------------
  const [qrCodesDownloading, setQrCodesDownloading] = useState(false);

  // --------------- Handlers ---------------

  const onSearch = useCallback((value: string) => {
    branchesStore.setFilters({ search: value });
  }, []);

  const onSelectAvailability = useCallback((options: IPickerOption[]) => {
    const { value } = getLastArrayItem<IPickerOption>(options) || {};
    branchesStore.setFilters({ availability: value as BRANCHES_AVAILABILITY_OPTION });
  }, []);

  /**
   * Download list of QR-codes in PDF
   */
  const onPressDownloadQrCode = async () => {
    try {
      setQrCodesDownloading(true);

      // data can be blob or link
      const { data, headers } = await client.get(
        'v4/branches/qr/pdf',
        { params: { file: booleanToNumber(!window.IS_MOBILE) }, responseType: window.IS_MOBILE ? 'text' : 'blob' },
        true
      );

      if (window.IS_MOBILE) {
        wms.downloadFile(data);
      } else {
        downloadFile(data, extractFileName(headers));
      }
    } catch (error: any) {
      logger.error({ error, place: 'BranchesHeader onPressDownloadQrCode' });
    } finally {
      setQrCodesDownloading(false);
    }
  };

  const onPressCreation = () => {
    history.push(SCREENS.BRANCHES_CREATE());
  };

  // --------------- Other ---------------

  const selectedPickerOption = useMemo(() => {
    const { availability } = branchesStore.filters;
    return availability ? [{ label: localeStore.t(availability), value: availability }] : [];
  }, [branchesStore.filters.availability]);

  return (
    <div className="d-flex flex-row justify-content-between pr-4">
      <div className="d-flex flex-row gap-10">
        {!branchesStore.isEmptyBranchesList && (
          <SearchInput
            value={branchesStore.filters.search}
            onChangeText={onSearch}
            debounceDelay={DEBOUNCE_TIME.MEDIUM}
            loading={branchesStore.isLoading}
            withResetIcon
            customPlaceholder
            placeholder="txt_search"
            className="branches-header__search"
            inputClassName="color-dark text-size-16 family-medium"
          />
        )}
        <Picker
          options={branchesFilterOptions}
          selectedOptions={selectedPickerOption}
          placeholder={localeStore.t('select_availability')}
          listClassName="branches-header__filter"
          onSelect={onSelectAvailability}
        />
      </div>
      <div className="d-flex gap-10">
        {appStore.isAddonsAvailable.emenu && (
          <Button
            title={localeStore.t('download_branches_qr_codes')}
            onClick={onPressDownloadQrCode}
            loading={qrCodesDownloading || branchesStore.isLoading}
            appearance="tertiary"
          />
        )}
        <IntegrationManager setting={integrationsStore.posRestrictions?.branchCreate} withTooltip tooltipOptions={{ minWidth: 350 }}>
          <Button title={localeStore.t('create_branch_button')} onClick={onPressCreation} loading={branchesStore.isLoading} />
        </IntegrationManager>
      </div>
    </div>
  );
});

export default BranchesHeader;
